import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Image, 
  Video, 
  Filter, 
  Search, 
  Users,
  Heart,
  Share2,
  Download,
  X,
  Copy,
  ExternalLink,
  MessageCircle,
  Loader,
  Grid,
  Folder,
  Calendar,
  ArrowLeft,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import ImagePlaceholder from '../components/common/ImagePlaceholder';
import FeaturedGallery from '../components/common/FeaturedGallery';
import api from '../config/api';
import HeroSection from '../components/common/HeroSection'; // Added import for HeroSection

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [likedItems, setLikedItems] = useState(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'albums'
  const [eventAlbums, setEventAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  // Fetch logo from about page content
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.get('/about-page');
        if (response.ok) {
          const data = await response.json();
          setLogoUrl(data.logo || '');
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    fetchLogo();
  }, []);

  const categories = [
    { value: 'all', label: 'All Media', count: 0 },
    { value: 'events', label: 'Events', count: 0 },
    { value: 'workshops', label: 'Workshops', count: 0 },
    { value: 'community', label: 'Community', count: 0 },
    { value: 'performances', label: 'Performances', count: 0 }
  ];

  // Fetch event albums (for both albums view and all media view)
  useEffect(() => {
    const fetchEventAlbums = async () => {
      try {
        const response = await api.get('/gallery/albums/events');
        if (response.ok) {
          const data = await response.json();
          setEventAlbums(data);
        }
      } catch (err) {
        console.error('Error fetching event albums:', err);
      }
    };
    
    fetchEventAlbums();
  }, []);

  // Fetch gallery data from backend
  useEffect(() => {
    const fetchGalleryItems = async () => {
      try {
        setLoading(true);
        const response = await api.get('/gallery');
        if (!response.ok) {
          throw new Error('Failed to fetch gallery items');
        }
        const data = await response.json();
        
        // Filter out items without valid image URLs and transform the data
        const transformedData = data
          .filter(item => item.imageUrl && item.imageUrl.trim() !== '') // Filter out items without image URLs
          .map(item => ({
            id: item.id,
            title: item.title || 'Untitled',
            type: item.imageUrl?.includes('.mp4') || item.imageUrl?.includes('.mov') || item.imageUrl?.includes('.avi') ? 'video' : 'image',
            category: item.album || 'general',
            eventId: item.eventId || '',
            url: item.imageUrl,
            thumbnail: item.imageUrl, // Use the same URL for thumbnail
            createdAt: item.createdAt,
            likes: 0,
            views: 0
          }));
        
        // Sort by creation date (newest first)
        const sortedData = transformedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setGalleryItems(sortedData);
        
        // Update category counts (only for non-event media)
        const categoryCounts = {};
        transformedData.filter(i => !i.eventId).forEach(item => {
          categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1;
        });
        
        categories.forEach(cat => {
          if (cat.value !== 'all') {
            cat.count = categoryCounts[cat.value] || 0;
          }
        });
        // All Media count shows only non-event items
        categories[0].count = transformedData.filter(i => !i.eventId).length;
        
      } catch (err) {
        console.error('Error fetching gallery items:', err);
        setError(err.message);
        toast.error('Failed to load gallery items');
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryItems();
  }, []);

  // In All Media view, hide event-linked items (albums will represent them)
  const baseItems = galleryItems.filter(i => !i.eventId);

  const filteredItems = baseItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = (item.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (item.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const getActiveMediaList = useCallback(() => {
    if (selectedAlbum) {
      return selectedAlbum.photos.map(photo => ({
        id: photo.id,
        title: photo.title || 'Untitled',
        type: photo.imageUrl?.includes('.mp4') || photo.imageUrl?.includes('.mov') || photo.imageUrl?.includes('.avi') ? 'video' : 'image',
        url: photo.imageUrl,
        thumbnail: photo.imageUrl,
        createdAt: photo.createdAt
      }));
    }
    return filteredItems;
  }, [selectedAlbum, filteredItems]);

  const handleNextMedia = useCallback((e) => {
    if (e) e.stopPropagation();
    const mediaList = getActiveMediaList();
    if (mediaList.length <= 1) return;
    const currentIndex = mediaList.findIndex(item => item.id === selectedMedia?.id);
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % mediaList.length;
    setSelectedMedia(mediaList[nextIndex]);
  }, [getActiveMediaList, selectedMedia]);

  const handlePrevMedia = useCallback((e) => {
    if (e) e.stopPropagation();
    const mediaList = getActiveMediaList();
    if (mediaList.length <= 1) return;
    const currentIndex = mediaList.findIndex(item => item.id === selectedMedia?.id);
    if (currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + mediaList.length) % mediaList.length;
    setSelectedMedia(mediaList[prevIndex]);
  }, [getActiveMediaList, selectedMedia]);

  const openModal = (item) => {
    setSelectedMedia(item);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  // Keyboard navigation for gallery modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;
      if (e.key === 'ArrowRight') {
        handleNextMedia();
      } else if (e.key === 'ArrowLeft') {
        handlePrevMedia();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedMedia, handleNextMedia, handlePrevMedia]);

  // Like functionality
  const handleLike = (itemId, e) => {
    e.stopPropagation();
    const newLikedItems = new Set(likedItems);
    
    if (newLikedItems.has(itemId)) {
      newLikedItems.delete(itemId);
      toast.success('Removed from favorites');
    } else {
      newLikedItems.add(itemId);
      toast.success('Added to favorites');
    }
    
    setLikedItems(newLikedItems);
    
    // Update likes count
    setGalleryItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, likes: newLikedItems.has(itemId) ? item.likes + 1 : item.likes - 1 }
        : item
    ));
  };

  // Share functionality
  const handleShare = (item, e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/gallery/${item.id}`;
    setShareUrl(shareUrl);
    setShowShareModal(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
      setShowShareModal(false);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareOnSocialMedia = (platform) => {
    const text = `Check out this amazing content from Beats of Washington: ${selectedMedia?.title}`;
    const url = shareUrl;
    
    let shareUrl_platform = '';
    switch (platform) {
      case 'facebook':
        shareUrl_platform = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl_platform = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl_platform = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl_platform, '_blank', 'width=600,height=400');
    setShowShareModal(false);
    toast.success(`Shared on ${platform}!`);
  };

  // Download functionality
  const handleDownload = async (item, e) => {
    e.stopPropagation();
    
    try {
      if (item.type === 'image') {
        // For images, create a download link
        const link = document.createElement('a');
        link.href = item.url;
        link.download = `${item.title.replace(/\s+/g, '_')}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Image downloaded successfully!');
      } else if (item.type === 'video') {
        // For videos, we'll show a message since direct download might not work with external URLs
        toast.success('Video download started!');
        // In a real app, you'd have the video file on your server
        const link = document.createElement('a');
        link.href = item.url;
        link.download = `${item.title.replace(/\s+/g, '_')}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      toast.error('Download failed. Please try again.');
    }
  };

  const isLiked = (itemId) => likedItems.has(itemId);

  return (
    <>
      <Helmet>
        <title>Gallery - Beats of Washington</title>
        <meta name="description" content="Explore our photo and video gallery showcasing community events, workshops, performances, and the people who make BOW special." />
      </Helmet>

      {/* Hero Section */}
      <HeroSection
        title="Our Gallery"
        description="Explore photos and videos from our community events, workshops, and performances that bring people together through music."
        badge="📸 Capture & Share ��"
        logoUrl={logoUrl}
        showLogo={true}
        floatingElements={[
          { icon: Image, position: 'top-10 left-10', animation: 'animate-float-slow' },
          { icon: Video, position: 'top-20 right-32', animation: 'animate-float-slow-reverse' },
          { icon: Heart, position: 'bottom-20 left-32', animation: 'animate-float-slow' },
          { icon: Share2, position: 'bottom-32 right-10', animation: 'animate-float-slow-reverse' }
        ]}
        interactiveElements={[
          { icon: Image, label: 'Photos', color: 'text-green-300' },
          { icon: Video, label: 'Videos', color: 'text-blue-300' },
          { icon: Heart, label: 'Memories', color: 'text-pink-300' }
        ]}
      />

      {/* Filters and Search */}
      <section className="bg-white py-4 sm:py-6 md:py-8 border-b">
        <div className="container-custom px-4 sm:px-6">
          {/* View Mode Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  setViewMode('all');
                  setSelectedAlbum(null);
                }}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors flex items-center gap-1.5 sm:gap-2 ${
                  viewMode === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">All Media</span>
              </button>
              <button
                onClick={() => setViewMode('albums')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors flex items-center gap-1.5 sm:gap-2 ${
                  viewMode === 'albums'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Folder className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">Albums by Event</span>
              </button>
            </div>
            {selectedAlbum && (
              <button
                onClick={() => setSelectedAlbum(null)}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Albums
              </button>
            )}
          </div>

          {!selectedAlbum && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {/* Search */}
            <div className="sm:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search gallery..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

              {/* Category Filter - Only show in 'all' view */}
              {viewMode === 'all' && (
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label} ({category.count})
                  </option>
                ))}
              </select>
            </div>
              )}
          </div>
          )}
        </div>
      </section>

      {/* Event Albums View */}
      {viewMode === 'albums' && !selectedAlbum && (
        <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
          <div className="container-custom px-4 sm:px-6">
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Event Albums
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                Browse photos organized by event
              </p>
            </div>

            {eventAlbums.length === 0 ? (
              <div className="text-center py-12 sm:py-16 md:py-20">
                <Folder className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No Event Albums Yet</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 px-4">
                  Upload photos and assign them to events to see albums here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {eventAlbums.map((album) => (
                  <div
                    key={album.eventId}
                    onClick={() => setSelectedAlbum(album)}
                    className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-shadow duration-300 group"
                  >
                    {/* Album Cover Image */}
                    <div className="relative h-40 sm:h-48 overflow-hidden">
                      {album.eventImage ? (
                        <img
                          src={album.eventImage}
                          alt={album.eventTitle}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : album.photos.length > 0 ? (
                        <img
                          src={album.photos[0].imageUrl}
                          alt={album.eventTitle}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-600 flex items-center justify-center">
                          <Folder className="w-12 h-12 sm:w-16 sm:h-16 text-white opacity-50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white">
                        <h3 className="text-base sm:text-xl font-bold mb-1 break-words">{album.eventTitle}</h3>
                        {album.eventDate && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span>{new Date(album.eventDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Album Info */}
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">
                          {album.photos.length} {album.photos.length === 1 ? 'photo' : 'photos'}
                        </span>
                        <span className="text-primary-600 font-medium group-hover:text-primary-700 text-xs sm:text-sm">
                          View Album →
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Selected Album View */}
      {selectedAlbum && (
        <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
          <div className="container-custom px-4 sm:px-6">
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 break-words">
                {selectedAlbum.eventTitle}
              </h2>
              {selectedAlbum.eventDate && (
                <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2 flex-wrap">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="break-words">{new Date(selectedAlbum.eventDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </p>
              )}
            </div>

            {selectedAlbum.photos.length === 0 ? (
              <div className="text-center py-12 sm:py-16 md:py-20">
                <Image className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-600">No photos in this album yet.</p>
              </div>
            ) : (
              <FeaturedGallery photos={selectedAlbum.photos} />
            )}
          </div>
        </section>
      )}

      {/* Gallery Grid - All Media View */}
      {viewMode === 'all' && !selectedAlbum && (
      <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
        <div className="container-custom px-4 sm:px-6">
          {/* Event Albums Section */}
          {eventAlbums.length > 0 && (
            <div className="mb-8 sm:mb-12">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Event Albums</h2>
                <p className="text-sm sm:text-base text-gray-600">Browse photos organized by events</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {eventAlbums.map((album) => (
                  <div
                    key={album.eventId}
                    onClick={() => setSelectedAlbum(album)}
                    className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-shadow duration-300 group"
                  >
                    {/* Album Cover Image */}
                    <div className="relative h-40 sm:h-48 overflow-hidden">
                      {album.eventImage ? (
                        <img
                          src={album.eventImage}
                          alt={album.eventTitle}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : album.photos.length > 0 ? (
                        <img
                          src={album.photos[0].imageUrl}
                          alt={album.eventTitle}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-600 flex items-center justify-center">
                          <Folder className="w-12 h-12 sm:w-16 sm:h-16 text-white opacity-50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white">
                        <h3 className="text-base sm:text-xl font-bold mb-1 break-words">{album.eventTitle}</h3>
                        {album.eventDate && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span>{new Date(album.eventDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Album Info */}
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">
                          {album.photos.length} {album.photos.length === 1 ? 'photo' : 'photos'}
                        </span>
                        <span className="text-primary-600 font-medium group-hover:text-primary-700 text-xs sm:text-sm">
                          View Album →
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Individual Media Items Section */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {loading ? 'Loading...' : `${filteredItems.length} Items Found`}
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              {loading ? 'Fetching gallery items...' : (searchTerm && `Searching for "${searchTerm}"`)}
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading gallery items...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Gallery</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredItems.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Image className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Gallery Items Found</h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm 
                    ? `No items match your search for "${searchTerm}"`
                    : 'No images or videos have been uploaded to the gallery yet.'
                  }
                </p>
                {!searchTerm && (
                  <p className="text-sm text-gray-500">
                    Check back later or contact an administrator to upload content.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Gallery Items Grid */}
          {!loading && !error && filteredItems.length > 0 && (
            <FeaturedGallery photos={filteredItems} />
          )}
        </div>
      </section>
      )}

      {/* Media Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4">
          {/* Navigation Arrows (Screen Level) */}
          {getActiveMediaList().length > 1 && (
            <>
              <button
                onClick={handlePrevMedia}
                className="absolute left-2 md:left-4 lg:left-[calc(50%-480px)] top-1/2 transform -translate-y-1/2 z-50 bg-black/60 hover:bg-black/80 text-white p-2.5 sm:p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-2xl flex items-center justify-center backdrop-blur-md border border-white/10"
                aria-label="Previous media"
              >
                <ChevronLeft className="w-5 h-5 sm:w-8 sm:h-8" />
              </button>
              <button
                onClick={handleNextMedia}
                className="absolute right-2 md:right-4 lg:right-[calc(50%-480px)] top-1/2 transform -translate-y-1/2 z-50 bg-black/60 hover:bg-black/80 text-white p-2.5 sm:p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-2xl flex items-center justify-center backdrop-blur-md border border-white/10"
                aria-label="Next media"
              >
                <ChevronRight className="w-5 h-5 sm:w-8 sm:h-8" />
              </button>
            </>
          )}

          <div className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative z-10">
            <div className="relative">
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 bg-black/50 text-white rounded-full p-1.5 sm:p-2 hover:bg-black/70 transition-colors duration-200"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              
              {/* Media Content */}
              <div className="relative">
                {selectedMedia.type === 'video' ? (
                  <video
                    src={selectedMedia.url}
                    controls
                    className="w-full h-auto min-h-[200px] sm:h-[400px] md:h-96 object-contain bg-black rounded-t-xl sm:rounded-t-2xl"
                    playsInline
                  />
                ) : (
                  <ImagePlaceholder
                    src={selectedMedia.url}
                    alt={selectedMedia.title}
                    className="w-full h-auto min-h-[200px] sm:h-[400px] md:h-96 object-contain bg-gray-100 rounded-t-xl sm:rounded-t-2xl"
                    placeholderClassName="w-full h-auto min-h-[200px] sm:h-[400px] md:h-96 bg-gray-100 flex items-center justify-center rounded-t-xl sm:rounded-t-2xl"
                  />
                )}
              </div>
              
              {/* Media Info */}
              <div className="p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 break-words">
                  {selectedMedia.title}
                </h2>
                
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <button 
                    onClick={(e) => handleLike(selectedMedia.id, e)}
                    className={`flex items-center transition-colors duration-200 text-sm sm:text-base ${
                      isLiked(selectedMedia.id) 
                        ? 'text-red-600' 
                        : 'text-gray-600 hover:text-red-600'
                    }`}
                  >
                    <Heart className={`w-4 h-4 sm:w-5 sm:h-5 mr-1 ${isLiked(selectedMedia.id) ? 'fill-current' : ''}`} />
                    {selectedMedia.likes}
                  </button>
                  <button 
                    onClick={(e) => handleShare(selectedMedia, e)}
                    className="flex items-center text-sm sm:text-base text-gray-600 hover:text-primary-600 transition-colors duration-200"
                  >
                    <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                    Share
                  </button>
                  <button 
                    onClick={(e) => handleDownload(selectedMedia, e)}
                    className="flex items-center text-sm sm:text-base text-gray-600 hover:text-green-600 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Share This Content</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              {/* Copy Link */}
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg bg-gray-50"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              
              {/* Social Media Buttons */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <button
                  onClick={() => shareOnSocialMedia('facebook')}
                  className="flex flex-col sm:flex-row items-center justify-center px-2 sm:px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2 mb-1 sm:mb-0" />
                  <span className="hidden sm:inline">Facebook</span>
                  <span className="sm:hidden">FB</span>
                </button>
                <button
                  onClick={() => shareOnSocialMedia('twitter')}
                  className="flex flex-col sm:flex-row items-center justify-center px-2 sm:px-4 py-2 text-xs sm:text-sm bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors duration-200"
                >
                  <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2 mb-1 sm:mb-0" />
                  <span className="hidden sm:inline">Twitter</span>
                  <span className="sm:hidden">TW</span>
                </button>
                <button
                  onClick={() => shareOnSocialMedia('linkedin')}
                  className="flex flex-col sm:flex-row items-center justify-center px-2 sm:px-4 py-2 text-xs sm:text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors duration-200"
                >
                  <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2 mb-1 sm:mb-0" />
                  <span className="hidden sm:inline">LinkedIn</span>
                  <span className="sm:hidden">LI</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container-custom px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
            Share Your Memories
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Have photos or videos from BOW events? We'd love to see them! 
            Share your memories with our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="/contact" className="btn-primary text-sm sm:text-base md:text-lg px-6 sm:px-8 py-3 sm:py-4 inline-flex items-center justify-center">
              <Image className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Share Your Media
            </a>
            <a href="/events" className="btn-outline text-sm sm:text-base md:text-lg px-6 sm:px-8 py-3 sm:py-4 inline-flex items-center justify-center">
              <Video className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Attend Events
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default GalleryPage; 